/**
 * Bot seats. Basic strategy at three fidelities.
 *
 * The philosophy is the poker room's, and it is deliberate: fidelity is a TABLE-level
 * choice, never a per-seat label. "Who's at the table: Sharps / Regulars / Tourists"
 * tells you what kind of game it is; nothing on screen tells you which chair is the
 * weak one, because a product that labels the fish is teaching you to read a label
 * rather than a player.
 *
 * Identity and fidelity are drawn from independent streams, so a name never predicts a
 * skill level across sessions — same rule as `engine/cast.ts`.
 *
 * ## Why the bots deviate on the classic mistakes specifically
 *
 * A bot that deviates uniformly at random is noise, and noise teaches nothing. These
 * deviate the way people actually do: standing on stiff totals against a big card
 * because busting feels worse than losing, not hitting soft eighteen, refusing to
 * split eights against a ten, and taking insurance. Those are the errors a player will
 * see at a real table and the ones the third-base lesson is about.
 */
import { handTotal, type Action, type BlackjackTable } from "./table";
import { ACE_SLOT, rankSlot, SLOT_VALUE } from "./rules";
import { CryptoRng } from "../rng";

export type Fidelity = "sharp" | "casual" | "loose";

/** Deviation rate on the classic mistakes. Sharp never deviates. */
const DEVIATION: Record<Fidelity, number> = { sharp: 0, casual: 0.08, loose: 0.28 };

/**
 * Basic strategy for the shipped double-deck S17 game.
 *
 * Derived from `analysis.ts` against a full shoe rather than typed from a chart, and
 * `tests/blackjack-oracle.test.ts` checks it cell-for-cell against the published table
 * — so this table and the exact analysis cannot drift apart, and if they ever disagree
 * the test says which cell.
 */
export function basicAction(
  cards: readonly number[], upSlot: number, legal: readonly Action[],
): Action {
  const { total, soft } = handTotal(cards as number[]);
  const pair = cards.length === 2 && rankSlot(cards[0]! >> 2) === rankSlot(cards[1]! >> 2);
  const up = SLOT_VALUE[upSlot]!;                 // 2..11
  const can = (a: Action) => legal.includes(a);

  if (pair && can("split")) {
    const s = rankSlot(cards[0]! >> 2);
    if (s === ACE_SLOT) return "split";
    const v = SLOT_VALUE[s]!;
    if (v === 8) return "split";
    if (v === 10) { /* never split tens */ }
    else if (v === 9) { if (up <= 9 && up !== 7) return "split"; }
    else if (v === 7) { if (up <= 7) return "split"; }
    else if (v === 6) { if (up <= 6) return "split"; }
    else if (v === 4) { if (up === 5 || up === 6) return "split"; }
    else if (v === 3 || v === 2) { if (up <= 7) return "split"; }
  }

  if (soft) {
    if (total >= 19) return "stand";
    if (total === 18) {
      if (up >= 3 && up <= 6 && can("double")) return "double";
      return up >= 9 ? "hit" : "stand";
    }
    if (total === 17) return up >= 3 && up <= 6 && can("double") ? "double" : "hit";
    if (total === 16 || total === 15) return up >= 4 && up <= 6 && can("double") ? "double" : "hit";
    if (total === 14 || total === 13) return up >= 5 && up <= 6 && can("double") ? "double" : "hit";
    return "hit";
  }

  if (total >= 17) return "stand";
  if (total >= 13) return up <= 6 ? "stand" : "hit";
  if (total === 12) return up >= 4 && up <= 6 ? "stand" : "hit";
  if (total === 11) return can("double") && up !== 11 ? "double" : "hit";
  if (total === 10) return can("double") && up <= 9 ? "double" : "hit";
  if (total === 9) return can("double") && up >= 2 && up <= 6 ? "double" : "hit";
  return "hit";
}

/** The mistakes people actually make, in the order a table makes them. */
function classicMistake(
  cards: readonly number[], upSlot: number, legal: readonly Action[], correct: Action,
): Action | null {
  const { total, soft } = handTotal(cards as number[]);
  const up = SLOT_VALUE[upSlot]!;
  const has = (a: Action) => legal.includes(a);
  if (!soft && total >= 12 && total <= 16 && up >= 7 && correct === "hit") return "stand";
  if (soft && total === 18 && correct === "hit" && has("stand")) return "stand";
  if (correct === "split" && has("hit")) return "hit";
  if (correct === "double" && has("hit")) return "hit";
  if (!soft && total === 12 && up <= 3 && correct === "hit" && has("stand")) return "stand";
  return null;
}

export function botAction(
  table: BlackjackTable, fidelity: Fidelity, rng: CryptoRng,
): Action {
  const legal = table.legalActions();
  const seat = table.seats[table.turn!.seat]!;
  const hand = seat.hands[table.turn!.hand]!;
  const upSlot = rankSlot(table.dealerUp! >> 2);
  const correct = basicAction(hand.cards, upSlot, legal);
  const rate = DEVIATION[fidelity];
  if (rate > 0 && rng.next() < rate) {
    const slip = classicMistake(hand.cards, upSlot, legal, correct);
    if (slip && legal.includes(slip)) return slip;
  }
  return legal.includes(correct) ? correct : "stand";
}

/** Bots decline insurance at every fidelity except loose, where some take it. */
export function botTakesInsurance(fidelity: Fidelity, rng: CryptoRng): boolean {
  return fidelity === "loose" ? rng.next() < 0.35 : false;
}
